import { practice, formatOpeningHoursSpecification } from "../../data/practice";

const ORG_ID = `${practice.baseUrl}/#organization`;

export const organizationId = ORG_ID;

export function buildPostalAddress() {
  return {
    "@type": "PostalAddress" as const,
    streetAddress: practice.address.street,
    addressLocality: practice.address.locality,
    addressRegion: practice.address.region,
    postalCode: practice.address.postalCode,
    addressCountry: practice.address.country,
  };
}

export function buildGeo() {
  return {
    "@type": "GeoCoordinates" as const,
    latitude: practice.geo.latitude,
    longitude: practice.geo.longitude,
  };
}

export function buildContactPoints() {
  return [
    {
      "@type": "ContactPoint" as const,
      contactType: "customer service",
      email: practice.emails.primary,
      telephone: practice.phone,
      availableLanguage: "German",
    },
    {
      "@type": "ContactPoint" as const,
      contactType: "customer service",
      email: practice.emails.secondary,
      telephone: practice.phone,
      availableLanguage: "German",
    },
  ];
}

export interface MedicalBusinessOptions {
  url?: string;
  description?: string;
  medicalSpecialty?: string | string[];
  image?: string;
  includeOfferCatalog?: boolean;
  includeFounder?: boolean;
  extra?: Record<string, unknown>;
}

export function buildMedicalBusiness(options: MedicalBusinessOptions = {}) {
  const {
    url = practice.baseUrl,
    description = practice.description,
    medicalSpecialty = practice.medicalSpecialties,
    image = `${practice.baseUrl}${practice.defaultImage}`,
    includeOfferCatalog = false,
    includeFounder = false,
    extra = {},
  } = options;

  const entity: Record<string, unknown> = {
    "@type": "MedicalBusiness",
    "@id": ORG_ID,
    name: practice.name,
    alternateName: practice.alternateName,
    description,
    url,
    telephone: practice.phone,
    email: practice.emails.primary,
    address: buildPostalAddress(),
    geo: buildGeo(),
    openingHoursSpecification: formatOpeningHoursSpecification(),
    priceRange: practice.priceRange,
    image,
    medicalSpecialty: medicalSpecialty,
    areaServed: {
      "@type": "City",
      name: practice.areaServed,
    },
    sameAs: [practice.instagram.url],
    contactPoint: buildContactPoints(),
    ...extra,
  };

  if (includeFounder) {
    entity.founder = {
      "@type": "Person",
      name: practice.founder.name,
      jobTitle: practice.founder.jobTitle,
    };
  }

  if (includeOfferCatalog) {
    entity.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: "Ergotherapie Leistungen",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "MedicalTherapy",
            name: "Neurologische Ergotherapie",
            description:
              "Behandlung bei Schlaganfall, MS, Parkinson und anderen neurologischen Erkrankungen",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "MedicalTherapy",
            name: "Psychiatrische Ergotherapie",
            description: "Therapie bei Depression, Burnout und Angststörungen",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "MedicalTherapy",
            name: "Geriatrische Ergotherapie",
            description: "Therapie für Senioren bei Demenz und Alterserkrankungen",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "MedicalTherapy",
            name: "Pädiatrische Ergotherapie",
            description: "Kindertherapie bei ADHS und Entwicklungsstörungen",
          },
        },
      ],
    };
  }

  return entity;
}
